import { memo, ReactNode } from 'react';
import { ColumnDef, RowDef } from '../Table/types';
import { ExpandableTableRow, LoadingRow, TableHeadCell } from './components';
import { FooterTableRow } from './components/FooterTableRow/FooterTableRow';
export type ExpandableRowDef<T> = RowDef<T> & {
  rows?: Array<ExpandableRowDef<T>>;
};

type ExpandableTableTableProps<T> = {
  columns: Array<ColumnDef<T>>;
  rows: Array<ExpandableRowDef<T>>;
  emptyStateMessage: ReactNode;
  isLoading?: boolean;
  loadingRows?: number;
  footerRow?: ExpandableRowDef<T>;
  footerRowClassName?: string;
};

const ExpandableTableComponent = <T,>({
  columns,
  rows,
  emptyStateMessage,
  isLoading,
  loadingRows,
  footerRow,
  footerRowClassName,
}: ExpandableTableTableProps<T>) => {
  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr>
            {columns.map((column) => (
              <TableHeadCell key={column.id} cell={column} />
            ))}
          </tr>
        </thead>
        <tbody>
          {!isLoading && !rows.length && !!emptyStateMessage && (
            <tr className="h-11">
              <td colSpan={columns.length} align="center" className="text-md">
                {emptyStateMessage}
              </td>
            </tr>
          )}
          {isLoading
            ? Array.from({ length: loadingRows ?? 0 }).map((_, rowIndex) => (
                <LoadingRow
                  key={`loading-${rowIndex}`}
                  rowIndex={rowIndex}
                  columnsCount={columns.length}
                />
              ))
            : rows.map((row) => (
                <ExpandableTableRow key={row.id} row={row} columns={columns} />
              ))}
        </tbody>
        {footerRow && (
          <tfoot>
            <FooterTableRow
              columns={columns}
              row={footerRow}
              className={footerRowClassName}
            />
          </tfoot>
        )}
      </table>
    </div>
  );
};

export const ExpandableTable = memo(
  ExpandableTableComponent,
) as typeof ExpandableTableComponent;
